import type { ReactNode } from "react";
import { use } from "react";
import { tv } from "tailwind-variants";

import { TooltipAttachedContext } from "./tooltip-attached-context";

// preflight already strips the button chrome; the UA stylesheet still
// centers button text, so inherit it like the span this replaces would
const tooltipTarget = tv({ base: "cursor-default [text-align:inherit]" });

// deliberately narrow: whatever is accepted here must also make sense on the
// fallback span, which is why there is no id, title or role
interface TooltipTargetProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly "aria-label"?: string;
}

/**
 * Trigger for a `CustomTooltip` on read-only content (an icon, chip or badge).
 * Renders an unstyled button so react-aria's `<Focusable>` gets an
 * interactive element and keyboard users can reach the tooltip. It is named
 * by its visible text; pass `aria-label` when it only holds an icon.
 */
export function TooltipTarget({
  className,
  children,
  "aria-label": ariaLabel,
  // the ref and focus/hover handlers react-aria's <Focusable> clones in
  ...focusableProps
}: TooltipTargetProps) {
  // CustomTooltip passes its child through untouched when there is no
  // content: a button there would be a nameless tab stop that does nothing,
  // and aria-label is not allowed on a span without a role
  if (!use(TooltipAttachedContext)) {
    return <span className={className}>{children}</span>;
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={tooltipTarget({ className })}
      {...focusableProps}
    >
      {children}
    </button>
  );
}
